import { Loading } from "@components/common/Loading";
import { FormInstanceRenderer } from "@nomena-raminoheritiana/front-form-builder";
import { Box } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import {  useEffect, type FC } from "react";
import { useLanguage } from "../../../../contexts/LanguageContext";
import translations from "./translations.json";
import { removeFieldGrid } from "@utils/removeFieldGrid";
type SupportedLanguage = keyof typeof translations;

interface OfferFormProps {
    onSubmit?: (data: any) => void;
    defaultValue?: any;
    formBuilderAccessToken: string | null;
    successMessage?: string;
    isReadOnly?: boolean;
}

const OfferApplicantForm: FC<OfferFormProps> = ({
    onSubmit,
    defaultValue,
    formBuilderAccessToken,
    successMessage,
    isReadOnly
}) => {

    const { language } = useLanguage();
    const content = translations[language as SupportedLanguage] || translations.en;

    const handleAfterSubmit = async (data: any) => {
        enqueueSnackbar(successMessage || content.success, { variant: 'success' });
        onSubmit && onSubmit(data);
    };

    useEffect(() => {
        const cleanDOM = () => {
            const container = document.getElementById('offer-applicant-form');
            if (!container) return;

            // 1. Supprimer l'élément .form-navigation
            if (isReadOnly) {
                const navigationEl = container.querySelector('.form-navigation');
                if (navigationEl) {
                    const gridEl = navigationEl.closest('.MuiGrid-root, [class*="Grid"]');
                    gridEl?.remove();
                }
                // 3. Supprimer tous les boutons situés dans .dynamic-field-group
                const groupButtons = container.querySelectorAll('.dynamic-field-group button');
                groupButtons.forEach(button => button.remove());
                
                const formNavInputs = container.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
                    '.form-field input, .form-field select, .form-field textarea'
                );
                formNavInputs.forEach(input => {
                    input.disabled = true;
                });
            }

            
            
            removeFieldGrid(container, 'otp');
            removeFieldGrid(container, 'status');
            removeFieldGrid(container, 'assesment');
            removeFieldGrid(container, 'technicalAssessment');
            removeFieldGrid(container, 'isAnsweredAllQuestions');
            removeFieldGrid(container, 'answeredTechnicalQuestions');
            removeFieldGrid(container, 'isAnsweredAllTechnicalQuestions');
            removeFieldGrid(container, 'submittedLanguage');

            return true;
        };

        // Exécution initiale
        cleanDOM();

        // Observation pour gérer le rendu asynchrone du FormInstanceRenderer
        const container = document.getElementById('offer-applicant-form');
        if (!container) return;

        const observer = new MutationObserver(() => {
            cleanDOM();
        });

        observer.observe(container, {
            childList: true,
            subtree: true
        });

        return () => observer.disconnect();
    }, [isReadOnly]);

    return (
        <Box 
            id="offer-applicant-form"
            sx={{ 
                bgcolor: '#161b22', 
                color: '#f8fafc', 
                p: { xs: 2, sm: 4 }, 
                borderRadius: 2, 
                border: '1px solid #21262d',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',

                /* 1. FORCE LA COULEUR DU TEXTE SAISI (VALUE) EN BLANC POUR MUI */
                '& .MuiInputBase-input, & .MuiOutlinedInput-input, & .MuiInputBase-inputMultiline': {
                    color: '#ffffff !important',
                    WebkitTextFillColor: '#ffffff !important', // Correction pour Safari / Chrome Autofill
                },

                /* 2. FORCE LA COULEUR POUR LES INPUTS ET TEXTAREAS NATIVE HTML */
                '& input, & select, & textarea': {
                    color: '#ffffff !important',
                    WebkitTextFillColor: '#ffffff !important',
                },

                /* 3. ARRIÈRE-PLAN DES CHAMPS */
                '& input, & select': {
                    backgroundColor: '#0d1117 !important',
                    borderColor: '#30363d !important',
                },

                /* 4. CORRECTION SPÉCIFIQUE DU TEXTAREA */
                '& textarea': {
                    backgroundColor: 'transparent !important',
                    border: 'none !important',
                    outline: 'none !important',
                    boxShadow: 'none !important',
                    fontFamily: 'inherit !important',
                    fontSize: 'inherit !important',
                },

                /* 5. WRAPPERS DE CHAMPS & BORDURES */
                '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
                    backgroundColor: '#0d1117 !important',
                    borderRadius: '8px',
                    '& fieldset': {
                        borderColor: '#30363d !important',
                    },
                    '&:hover fieldset': {
                        borderColor: '#1976d2 !important',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: '#1976d2 !important',
                    },
                },

                /* 6. LABELS ET PLACEHOLDERS */
                '& label, & .MuiFormLabel-root, & .MuiInputLabel-root': {
                    color: '#94a3b8 !important',
                },
                '& ::placeholder, & .MuiInputBase-input::placeholder': {
                    color: '#64748b !important',
                    opacity: '1 !important',
                    WebkitTextFillColor: '#64748b !important',
                }
            }}
        >
            <FormInstanceRenderer 
                formInstanceId={`${import.meta.env.VITE_OFFER_APPLICANT_FORM_INSTANCE_ID}`}
                defaultValues={defaultValue}
                showUnnamedFields={false}
                accessToken={formBuilderAccessToken || undefined}
                formLoader={() => <Loading />}
                onSubmit={(data) => {
                    handleAfterSubmit(data);
                }}
                lang={language}
            />
        </Box>
    );
}

export default OfferApplicantForm;